/**
 * The visit beacon (BOAT-1897).
 *
 * A visit is an ARRIVAL, not a page view. One beacon opens or extends a
 * session and nothing fires again until the session has lapsed, which is the
 * whole difference between a table the conversion report can read and one it
 * cannot afford to.
 *
 * This replaces the bare `landing` device signal from BOAT-1895 rather than
 * sitting beside it: the visit endpoint takes the same device block, so a
 * landing that sent both would be two requests writing the same device row.
 * The other device signals — add to cart, checkout start — are untouched and
 * still go through {@link useDeviceSignal}.
 */

import { recordVisit } from '~/queries'

/**
 * Matches the API's session window. Inside it the beacon would only extend a
 * visit that the order, cart and login requests are extending anyway, so
 * sending it again buys nothing.
 */
const VISIT_WINDOW_MS = 30 * 60 * 1000
const VISIT_STORAGE_KEY = 'tc_visit_sent_at'

const readNumber = (key: string) => {
  try {
    const value = window.localStorage.getItem(key)

    return value === null ? null : Number.parseInt(value, 10)
  } catch {
    return null
  }
}

const writeNumber = (key: string, value: number) => {
  try {
    window.localStorage.setItem(key, String(value))
  } catch {
    // Storage blocked: the beacon then fires on every landing, and the API's
    // own session window still folds them into one visit rather than many.
  }
}

export const useVisit = () => {
  const { devicePayload } = useDeviceSignal()
  const { utmPayload } = useOrderUtm()

  /**
   * Opens or extends the visit.
   *
   * Never rejects. The visitor is shopping, and an analytics call is not
   * allowed to interrupt that.
   */
  const recordLanding = async () => {
    if (import.meta.server) return

    const lastSentAt = readNumber(VISIT_STORAGE_KEY)
    const now = Date.now()

    if (lastSentAt !== null && now - lastSentAt < VISIT_WINDOW_MS) return

    // Carries the site gate and the visitor id with it: an empty payload means
    // capture is off for this brand or the browser gave us nothing to key on,
    // and either way there is no visit to record.
    const payload = await devicePayload()

    if (!payload.device) return

    writeNumber(VISIT_STORAGE_KEY, now)

    try {
      await recordVisit({
        device: payload.device,
        ...utmPayload(),
        landing_url: window.location.href,
        // Empty on a same-site navigation and on a direct arrival alike; the
        // API keeps the two apart by storing the host it parses out, which is
        // what stops the Direct bucket swallowing traffic we can name.
        referrer: document.referrer || undefined,
      })
    } catch {
      // Let the next landing try again rather than surface anything.
      writeNumber(VISIT_STORAGE_KEY, 0)
    }
  }

  return { recordLanding }
}
